import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { Edit, Trash2, Mail, MapPin, Calendar } from "lucide-react";

export interface ContactCardData {
  id: string;
  name: string;
  email: string;
  emailRecipients?: string[];
  suburb: string;
  reportFrequency: "weekly" | "fortnightly" | "monthly";
  isActive: boolean;
  unsubscribedAt?: string | null;
}

export interface ContactCardProps {
  contact: ContactCardData;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClick?: (id: string) => void;
}

export function ContactCard({
  contact,
  onEdit,
  onDelete,
  onClick,
}: ContactCardProps) {
  const handleCardClick = () => {
    onClick?.(contact.id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(contact.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(contact.id);
  };

  return (
    <Card
      sx={{
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { boxShadow: 4 } : undefined,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      onClick={onClick ? handleCardClick : undefined}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="div">
            {contact.name}
          </Typography>
          <Chip
            label={
              contact.unsubscribedAt
                ? "Unsubscribed"
                : contact.isActive
                  ? "Active"
                  : "Inactive"
            }
            color={
              contact.unsubscribedAt
                ? "error"
                : contact.isActive
                  ? "success"
                  : "default"
            }
            size="small"
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
          <Mail size={16} color="gray" style={{ marginTop: 2 }} />
          <Box>
            {contact.emailRecipients && contact.emailRecipients.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {contact.emailRecipients[0]}
                </Typography>
                {contact.emailRecipients.length > 1 && (
                  <Typography variant="caption" color="primary">
                    +{contact.emailRecipients.length - 1} more recipient
                    {contact.emailRecipients.length > 2 ? "s" : ""}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {contact.email}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <MapPin size={16} color="gray" />
          <Typography variant="body2" color="text.secondary">
            {contact.suburb}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Calendar size={16} color="gray" />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textTransform: "capitalize" }}
          >
            {contact.reportFrequency}
          </Typography>
        </Box>
      </CardContent>

      {(onEdit || onDelete) && (
        <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
          {onEdit && (
            <IconButton size="small" onClick={handleEdit} aria-label="Edit">
              <Edit size={18} />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              size="small"
              onClick={handleDelete}
              aria-label="Delete"
              color="error"
            >
              <Trash2 size={18} />
            </IconButton>
          )}
        </CardActions>
      )}
    </Card>
  );
}
